/* ==========================================================================
   PDP conversion layer — bundle selector, sticky bar meta, live low-stock.
   Vanilla JS. Reuses the theme's window.themeCore (EventBus, CartApi, utils).
   Never fires tracking events itself: every add goes through the theme's
   CartApi so Shopify / Meta / TikTok / Pinterest / Omnisend pixels see the
   same Ajax cart calls they already listen to.
   ========================================================================== */
(function () {
  'use strict';

  var SESSION_GIFT_FLAG = 'pdp:gift-unavailable';
  var SESSION_DISCOUNT_FLAG = 'pdp:bundle-discount-unverified';
  // The discount exists but did not stretch to the last set added (e.g. one use per
  // order). Claims stay hidden until the bag no longer carries that used discount.
  var SESSION_OFFER_USED_FLAG = 'pdp:bundle-offer-used';

  function session(key, value) {
    try {
      if (arguments.length === 2) {
        if (value === null) window.sessionStorage.removeItem(key);
        else window.sessionStorage.setItem(key, value);
        return value;
      }
      return window.sessionStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function whenThemeReady(fn) {
    if (window.themeCore && window.themeCore.loaded) {
      fn();
    } else {
      document.addEventListener('theme:all:loaded', fn, { once: true });
    }
  }

  function bus() {
    return window.themeCore && window.themeCore.EventBus;
  }

  function fallbackFormatMoney(cents, format) {
    var value = (Number(cents || 0) / 100).toFixed(2);
    var parts = value.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    var amount = parts.join('.');
    var fmt = format || '${{amount}}';
    if (/amount_no_decimals/.test(fmt)) amount = parts[0];
    return fmt.replace(/\{\{\s*\w+\s*\}\}/, amount).replace(/<[^>]*>/g, '');
  }

  function formatMoney(cents, format) {
    var utils = window.themeCore && window.themeCore.utils;
    if (utils && typeof utils.formatMoney === 'function') {
      try {
        return utils.formatMoney(cents, format);
      } catch (e) {
        /* fall through */
      }
    }
    return fallbackFormatMoney(cents, format);
  }

  function parseJSON(el) {
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  function readSelectedVariant(form) {
    var el = form && form.querySelector('[data-selected-variant]');
    return parseJSON(el);
  }

  function readProduct(form) {
    var el = form && form.querySelector('[data-js-product-json]');
    return parseJSON(el);
  }

  /* Read-only snapshot of the live cart (/cart.js). A plain GET on purpose: going
     through CartApi would emit 'cart:updated' and make the drawer re-fetch the
     whole section for nothing. Resolves null on any failure or after 2.5 s so a
     slow read never blocks the add. */
  function readCartSnapshot() {
    if (typeof window.fetch !== 'function') return Promise.resolve(null);
    var timer = new Promise(function (resolve) {
      window.setTimeout(function () { resolve(null); }, 2500);
    });
    var request = window.fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (response) { return response.ok ? response.json() : null; })
      .catch(function () { return null; });
    return Promise.race([request, timer]);
  }

  /* Product-level discount money sitting in the cart (Buy X get Y, amount off products). */
  function lineDiscountTotal(cart) {
    var sum = 0;
    if (cart && cart.items) {
      cart.items.forEach(function (item) { sum += Number(item.line_level_total_discount || 0); });
    }
    return Math.floor(sum);
  }

  var stockCache = new WeakMap();
  function stockInfo(section, variant) {
    // Returns {limit, tracked} for a variant using the Liquid-rendered stock map.
    if (!section || !variant) return { limit: Infinity, tracked: false, quantity: NaN };
    var map = stockCache.get(section);
    if (!map) {
      map = parseJSON(section.querySelector('[data-pdp-stock]')) || {};
      stockCache.set(section, map);
    }
    var entry = map[String(variant.id)];
    if (!entry) return { limit: Infinity, tracked: false, quantity: NaN };
    var quantity = Number(entry[0]);
    var policy = entry[1];
    var tracked = entry[2] === true;
    if (!tracked || policy === 'continue') return { limit: Infinity, tracked: tracked, quantity: quantity };
    return { limit: isNaN(quantity) ? Infinity : Math.max(0, quantity), tracked: true, quantity: quantity };
  }

  /* --------------------------------------------------------------------------
     Offer engine + bundle selector (2026-09-21 rebuild)
     - Every amount comes from live variant prices of the pieces chosen.
       'percent' tiers floor the discount like Shopify; 'bxgy_free_cheapest'
       tiers make the cheapest chosen piece free (how a Shopify "Buy X get Y"
       automatic discount allocates).
     - 'quantity' mode adds N of the selected variant. 'mix' mode shows one
       row per piece (style + option selects) fed by the Liquid-rendered
       [data-pdp-mix-products] JSON — no fetches.
     - One ADD_TO_CART_MANY request through the theme's CartApi, identical
       variants merged, then the cart is checked against the promised total.
     -------------------------------------------------------------------------- */
  function fillTokens(template, t, moneyFormat) {
    if (!template) return '';
    return String(template)
      .replace(/\[qty\]/g, String(t.qty))
      .replace(/\[total\]/g, formatMoney(t.total, moneyFormat))
      .replace(/\[each\]/g, formatMoney(t.each, moneyFormat))
      .replace(/\[save\]/g, formatMoney(t.savings, moneyFormat))
      .replace(/\[pct\]/g, String(t.pct));
  }

  function shortVariantTitle(variant) {
    if (!variant) return '';
    var title = variant.public_title || variant.title || '';
    return title === 'Default Title' ? '' : title;
  }

  function initBundles(root) {
    if (!root || root.dataset.pdpBundlesReady === 'true') return;
    root.dataset.pdpBundlesReady = 'true';

    var form = root.closest('form[data-js-product-form]');
    var section = root.closest('[data-section-type="product"]');
    if (!form || !section) return;

    var sectionId = section.getAttribute('data-section-id');
    var d = root.dataset;
    var moneyFormat = d.moneyFormat || (window.themeCore && window.themeCore.objects && window.themeCore.objects.shop && window.themeCore.objects.shop.money_format) || '${{amount}}';
    var eachLabel = d.eachLabel || 'each';
    var saveLabel = d.saveLabel || 'You save';
    var savePrefix = d.savePrefix || 'Save';
    var unitSingular = d.unitSingular || 'piece';
    var unitPlural = d.unitPlural || 'pieces';
    var giftVariantId = d.giftVariant ? Number(d.giftVariant) : 0;
    var giftLabel = d.giftLabel || 'Free gift';
    var giftProperty = d.giftProperty || 'Gift';
    var stockNoteTemplate = d.stockNote || 'Only [count] in stock';
    var offLabel = d.offLabel || 'off';
    var bundleMode = d.bundleMode === 'mix' ? 'mix' : 'quantity';
    var ctaSingle = d.ctaSingle || 'Add to bag — [total]';
    var ctaMulti = d.ctaMulti || 'Add [qty] to bag — [total]';
    var stickySingle = d.stickySingle || 'Add to bag — [total]';
    var stickyMulti = d.stickyMulti || 'Add [qty] — [total]';
    var stickySummary = d.stickySummary || '[qty] pieces';
    var stickyFreeBadge = d.stickyFreeBadge || '1 free';
    var hideExpress = d.hideExpress !== 'false';
    var pieceLabel = d.pieceLabel || 'Piece [n]';
    var freePieceLabel = d.freePieceLabel || 'FREE PIECE';
    var styleLabel = d.styleLabel || 'Style';
    var freeLabel = d.freeLabel || 'Free';
    var soldOutLabel = d.soldOutLabel || 'Sold out';
    var noStockOption = d.noStockOption || 'No more in stock';
    var stockBlockTemplate = d.stockBlock || 'Only [count] × [name] in stock — pick another size or style for [piece].';
    var verifyNotice = d.verifyNotice || 'The bundle discount did not apply — you are seeing regular prices. Nothing has been charged.';
    var usedNotice = d.usedNotice || 'This offer is already applied in your bag, so the pieces you just added are at regular price. Nothing has been charged.';
    var addErrorText = d.addError || 'Something went wrong. Please try again.';
    var regularPriceLabel = d.regularPriceLabel || 'Regular price';
    var pieceSelectedNote = d.pieceSelectedNote || 'Chosen above';
    // Every extra piece follows the main picker unless the shopper opens the mixer.
    // Without this the same "Ring size" and "Color" controls appear once per piece,
    // which reads as the page repeating itself rather than as a feature.
    var pieceSameNote = d.pieceSameNote || 'Same as above';
    var mixOpenLabel = d.mixOpenLabel || 'Mix sizes & colours';
    var mixCloseLabel = d.mixCloseLabel || 'Use the same for all';

    var tiers = Array.prototype.slice.call(root.querySelectorAll('[data-pdp-tier]'));
    var radios = tiers.map(function (tier) {
      return tier.querySelector('input[type="radio"]');
    });
    var savingsEl = root.querySelector('[data-pdp-savings]');
    var blockEl = root.querySelector('[data-pdp-block]');
    var noticeEl = root.querySelector('[data-pdp-notice]');
    var mixPanel = root.querySelector('[data-pdp-mix]');
    var hiddenQty = root.querySelector('[data-pdp-bundle-quantity]');
    var themeQtyInput = form.querySelector('[data-quantity-input]');
    var qtyInput = themeQtyInput || hiddenQty;
    var mainButton = form.querySelector('.add-to-cart[type="submit"]');
    var stickyButton = form.querySelector('.js-sticky-add-to-cart-button');
    var expressWrap = form.querySelector('.product-add-to-cart__additional-buttons');
    var mixProducts = bundleMode === 'mix' ? (parseJSON(root.querySelector('[data-pdp-mix-products]')) || []) : [];
    if (bundleMode === 'mix' && (!mixPanel || !mixProducts.length)) {
      bundleMode = 'quantity';
    }

    // If the merchant also added the theme's Quantity block, drive that input
    // instead and drop ours so the form never submits two quantity fields.
    if (themeQtyInput && hiddenQty) {
      hiddenQty.parentNode.removeChild(hiddenQty);
    }

    if (session(SESSION_GIFT_FLAG) === '1') {
      root.setAttribute('data-gift-unavailable', '');
    }
    if (session(SESSION_DISCOUNT_FLAG) === '1') {
      root.setAttribute('data-discount-unverified', '');
      console.warn('[PDP bundles] Savings labels are hidden for this session because a previous add to cart showed no matching automatic discount. Create the discounts in Shopify admin (Discounts → Automatic) or disable bundles in the Theme Editor.');
    } else if (session(SESSION_OFFER_USED_FLAG) === '1') {
      // The bag already used the offer once. Keep the claims hidden while that
      // discounted set is still in the bag; show them again once it is gone.
      root.setAttribute('data-discount-unverified', '');
      readCartSnapshot().then(function (cart) {
        if (!cart) return;
        if (Number(cart.item_count || 0) === 0 || lineDiscountTotal(cart) <= 0) {
          session(SESSION_OFFER_USED_FLAG, null);
          root.removeAttribute('data-discount-unverified');
          render();
        }
      });
    }

    var productJson = readProduct(form);
    var state = {
      variant: readSelectedVariant(form) || (productJson && productJson.variants ? productJson.variants[0] : null),
      // mix rows for pieces 2..n: { handle, variantId, touched }
      rows: [],
      // false = every extra piece mirrors the main picker and shows no controls of its own.
      // A template can start it open (data-mix-open-default) — necklaces do, because a
      // pair is usually two different colours.
      mixOpen: d.mixOpenDefault === 'true',
      blocked: '',
      submitting: false
    };

    /* ---------- product / variant lookups (mix mode) ---------- */
    var productByHandle = {};
    mixProducts.forEach(function (p) {
      productByHandle[p.h] = p;
      p.variants = (p.v || []).map(function (v) {
        return { id: Number(v[0]), options: v[1] || [], price: Number(v[2]), available: v[3] === true, quantity: Number(v[4]), policy: v[5], tracked: v[6] === true, product: p };
      });
      p.optionNames = (p.o || []).filter(function (name) { return name !== 'Title'; });
      p.hasOptions = p.optionNames.length > 0 && !(p.variants.length === 1 && p.variants[0].options.join('') === 'Default Title');
    });
    var currentProduct = mixProducts[0] || null;
    var currentHandle = currentProduct ? currentProduct.h : (productJson && productJson.handle) || '';

    function findVariant(handle, variantId) {
      var p = productByHandle[handle];
      if (!p) return null;
      for (var i = 0; i < p.variants.length; i += 1) {
        if (p.variants[i].id === Number(variantId)) return p.variants[i];
      }
      return null;
    }

    function variantByOptions(p, values) {
      for (var i = 0; i < p.variants.length; i += 1) {
        var v = p.variants[i];
        var match = true;
        for (var k = 0; k < values.length; k += 1) {
          if (values[k] !== null && v.options[k] !== values[k]) { match = false; break; }
        }
        if (match) return v;
      }
      return null;
    }

    function firstAvailable(p, prefer) {
      // Prefer variants that share option values (e.g. the same ring size) with `prefer`.
      var best = null;
      var bestScore = -1;
      p.variants.forEach(function (v) {
        if (!v.available) return;
        var score = 0;
        if (prefer && prefer.product) {
          p.optionNames.forEach(function (name, k) {
            var idx = prefer.product.optionNames.map(function (n) { return n.toLowerCase(); }).indexOf(name.toLowerCase());
            if (idx > -1 && prefer.options[idx] === v.options[k]) score += 1;
          });
        }
        if (score > bestScore) { best = v; bestScore = score; }
      });
      return best || p.variants[0] || null;
    }

    /* Stock limit for any variant: products JSON first, then the stock map. */
    function limitFor(variantLike) {
      if (!variantLike) return 0;
      if (variantLike.product) {
        if (!variantLike.available) return 0;
        if (!variantLike.tracked || variantLike.policy === 'continue') return Infinity;
        return isNaN(variantLike.quantity) ? Infinity : Math.max(0, variantLike.quantity);
      }
      if (!variantLike.available) return 0;
      return stockInfo(section, variantLike).limit;
    }

    /* Piece 1 is always the page's selected variant. */
    function mainPiece() {
      var v = state.variant;
      if (!v) return null;
      var enriched = currentProduct ? findVariant(currentHandle, v.id) : null;
      return {
        id: Number(v.id),
        price: Number(v.price),
        available: !!v.available,
        title: shortVariantTitle(v),
        product: currentProduct,
        limit: enriched ? limitFor(enriched) : limitFor(v),
        variant: enriched || v
      };
    }

    function rowPiece(row) {
      var v = findVariant(row.handle, row.variantId);
      if (!v) return null;
      return {
        id: v.id,
        price: v.price,
        available: v.available,
        title: v.options.join(' / ') === 'Default Title' ? '' : v.options.join(' / '),
        product: v.product,
        limit: limitFor(v),
        variant: v
      };
    }

    /* Pieces for a tier: in mix mode the real rows, otherwise N × the main variant. */
    function piecesFor(tier, data) {
      var main = mainPiece();
      var pieces = [];
      if (!main) return pieces;
      for (var i = 0; i < data.qty; i += 1) {
        if (i === 0 || bundleMode !== 'mix' || !isSelected(tier)) {
          pieces.push(main);
        } else {
          var row = state.rows[i - 1];
          pieces.push((row && rowPiece(row)) || main);
        }
      }
      return pieces;
    }

    function isSelected(tier) {
      var idx = tiers.indexOf(tier);
      return idx > -1 && !!(radios[idx] && radios[idx].checked);
    }

    function selectedTier() {
      for (var i = 0; i < tiers.length; i += 1) {
        if (radios[i] && radios[i].checked) return tiers[i];
      }
      return tiers[0] || null;
    }

    function tierData(tier) {
      return {
        qty: Math.max(1, parseInt(tier.dataset.qty, 10) || 1),
        discount: Math.min(100, Math.max(0, parseFloat(tier.dataset.discount) || 0)),
        offer: tier.dataset.offer === 'bxgy_free_cheapest' ? 'bxgy_free_cheapest' : 'percent',
        gift: tier.dataset.gift === 'true'
      };
    }

    /* The maths. Returns totals in cents, all from live variant prices. */
    function compute(data, pieces) {
      var claimsHidden = root.hasAttribute('data-discount-unverified');
      var subtotal = 0;
      var cheapest = Infinity;
      var cheapestIndex = -1;
      pieces.forEach(function (p, i) {
        subtotal += p.price;
        if (p.price <= cheapest) { cheapest = p.price; cheapestIndex = i; }
      });
      var savings = 0;
      if (!claimsHidden && pieces.length > 1) {
        if (data.offer === 'bxgy_free_cheapest') {
          savings = cheapest === Infinity ? 0 : cheapest;
        } else {
          // Shopify floors percentage discounts; mirror it so the page matches checkout.
          savings = Math.floor(subtotal * data.discount / 100);
        }
      }
      var total = subtotal - savings;
      var qty = Math.max(1, data.qty);
      return {
        qty: qty,
        subtotal: subtotal,
        savings: savings,
        total: total,
        each: Math.round(total / qty),
        pct: data.offer === 'bxgy_free_cheapest' ? (subtotal > 0 ? Math.round(savings * 100 / subtotal) : 0) : (claimsHidden ? 0 : data.discount),
        freeIndex: data.offer === 'bxgy_free_cheapest' && savings > 0 ? cheapestIndex : -1,
        bxgy: data.offer === 'bxgy_free_cheapest'
      };
    }

    function setText(el, text) {
      if (!el) return;
      el.textContent = text;
      el.hidden = !text;
    }

    /* ---------- stock guard (pieces per variant vs tracked+deny stock) ---------- */
    function stockProblem(pieces, bxgy) {
      var counts = {};
      var byId = {};
      var pieceIndex = {};
      pieces.forEach(function (p, i) {
        counts[p.id] = (counts[p.id] || 0) + 1;
        byId[p.id] = p;
        pieceIndex[p.id] = i;
      });
      var ids = Object.keys(counts);
      for (var i = 0; i < ids.length; i += 1) {
        var p = byId[ids[i]];
        var limit = p.available ? p.limit : 0;
        if (isFinite(limit) && counts[ids[i]] > limit) {
          var name = (p.product ? p.product.t : '') + (p.title ? ' · ' + p.title : '');
          var idx = pieceIndex[ids[i]];
          return {
            piece: p,
            index: idx,
            message: stockBlockTemplate
              .replace(/\[count\]/g, String(limit))
              .replace(/\[name\]/g, name.trim())
              .replace(/\[piece\]/g, rowLabel(idx, pieces.length, bxgy && idx === pieces.length - 1))
          };
        }
      }
      return null;
    }

    function rowLabel(index, qty, free) {
      if (free) return freePieceLabel;
      return pieceLabel.replace(/\[n\]/g, String(index + 1));
    }

    /* ---------- render everything ---------- */
    function render() {
      var variant = state.variant;
      var available = !!(variant && variant.available);
      var main = mainPiece();
      var needsFallback = false;

      tiers.forEach(function (tier, index) {
        var data = tierData(tier);
        var pieces = piecesFor(tier, data);
        var t = compute(data, pieces);
        var limit = main ? main.limit : 0;
        var inStock = available && (bundleMode === 'mix' || data.qty <= limit);

        var title = fillTokens(tier.dataset.tplTitle, t, moneyFormat) || tier.dataset.tplTitle;
        if (t.bxgy && t.savings <= 0 && root.hasAttribute('data-discount-unverified')) {
          // A "get 1 free" headline must not survive a cart that charged full price.
          title = data.qty + ' ' + (data.qty === 1 ? unitSingular : unitPlural);
        }
        setText(tier.querySelector('[data-bundle-title]'), title);
        setText(tier.querySelector('[data-bundle-sub]'), fillTokens(tier.dataset.tplSub, t, moneyFormat));
        setText(tier.querySelector('[data-bundle-badge]'), fillTokens(tier.dataset.tplBadge, t, moneyFormat));
        setText(tier.querySelector('[data-bundle-helper-text]'), fillTokens(tier.dataset.tplHelper, t, moneyFormat));
        var helper = tier.querySelector('[data-bundle-helper]');
        var helperShown = false;
        if (helper) {
          var helperText = fillTokens(tier.dataset.tplHelper, t, moneyFormat);
          // A "you get one free" line must vanish when no saving is being promised.
          helper.hidden = !helperText || (t.bxgy && t.savings <= 0);
          helperShown = !helper.hidden;
        }
        var limitNote = tier.querySelector('[data-bundle-limit]');
        if (limitNote) {
          // The discount's own limit ("one free piece per order") only makes sense next to the promise.
          limitNote.hidden = !helperShown || !(t.bxgy && t.savings > 0);
        }
        setText(tier.querySelector('[data-bundle-price]'), formatMoney(t.total, moneyFormat));
        var compareEl = tier.querySelector('[data-bundle-compare]');
        if (compareEl) {
          // The struck amount keeps its visually-hidden "Regular price" prefix for screen readers.
          var compareValue = compareEl.querySelector('[data-bundle-compare-value]') || compareEl;
          compareValue.textContent = t.savings > 0 ? formatMoney(t.subtotal, moneyFormat) : '';
          compareEl.hidden = !(t.savings > 0);
        }
        setText(tier.querySelector('[data-bundle-each]'), data.qty > 1 ? formatMoney(t.each, moneyFormat) + ' ' + eachLabel : '');
        var saveEl = tier.querySelector('[data-bundle-save]');
        if (saveEl) {
          saveEl.textContent = t.pct > 0 && !t.bxgy ? savePrefix + ' ' + t.pct + '%' : '';
          saveEl.hidden = !(t.pct > 0 && !t.bxgy);
        }
        var savingsNote = tier.querySelector('[data-bundle-savings]');
        if (savingsNote) {
          savingsNote.textContent = t.savings > 0 ? formatMoney(t.savings, moneyFormat) + ' ' + offLabel : '';
          savingsNote.hidden = !(t.savings > 0);
        }
        var stockNote = tier.querySelector('[data-bundle-stock]');
        if (stockNote) {
          var showNote = available && !inStock && isFinite(limit);
          stockNote.textContent = showNote ? stockNoteTemplate.replace(/\[count\]/g, String(limit)) : '';
          stockNote.hidden = !showNote;
        }

        tier.classList.toggle('is-unavailable', !inStock);
        tier.classList.toggle('is-selected', isSelected(tier));
        if (radios[index]) {
          radios[index].disabled = !inStock;
          if (!inStock && radios[index].checked) needsFallback = true;
        }
        tier.dataset.savingsCents = String(t.savings);
        tier.dataset.totalCents = String(t.total);
      });

      if (needsFallback) {
        // Move the selection to the largest tier that can actually be bought.
        // If nothing is buyable (sold out) leave the selection alone: no loop.
        var fallback = null;
        for (var i = tiers.length - 1; i >= 0; i -= 1) {
          if (radios[i] && !radios[i].disabled) { fallback = i; break; }
        }
        if (fallback !== null && radios[fallback] && !radios[fallback].checked) {
          radios[fallback].checked = true;
          window.setTimeout(syncQuantity, 0);
        }
      }

      var current = selectedTier();
      var currentData = current ? tierData(current) : { qty: 1, discount: 0, offer: 'percent', gift: false };
      var currentPieces = current ? piecesFor(current, currentData) : [];
      var totals = compute(currentData, currentPieces);

      if (savingsEl) {
        savingsEl.textContent = totals.savings > 0 ? saveLabel + ' ' + formatMoney(totals.savings, moneyFormat) : '';
      }

      renderMix(current, currentData, currentPieces, totals);
      tiers.forEach(function (tier) {
        var panel = tier.querySelector('[data-bundle-panel]');
        if (!panel) return;
        var helperEl = panel.querySelector('[data-bundle-helper]');
        var hasContent = !!(helperEl && !helperEl.hidden) || !!(mixPanel && mixPanel.parentNode === panel && !mixPanel.hidden);
        panel.classList.toggle('has-content', hasContent);
        tier.classList.toggle('has-panel', hasContent && isSelected(tier));
      });

      var problem = available && currentPieces.length ? stockProblem(currentPieces, totals.bxgy) : null;
      state.blocked = problem ? problem.message : '';
      if (blockEl) {
        blockEl.textContent = state.blocked;
        blockEl.hidden = !state.blocked;
      }

      root.dataset.selectedQty = String(currentData.qty);
      root.classList.toggle('pdp-bundles--multi', currentData.qty > 1);
      if (expressWrap) expressWrap.classList.toggle('pdp-express-hidden', hideExpress && currentData.qty > 1);

      applyCta(totals, currentData, available);
      pushSticky(totals, currentData, current, available);
    }

    /* ---------- main CTA label / disabled state ---------- */
    var ctaApplying = false;
    var ctaObserver = null;
    function ctaLabel(totals, data, available) {
      if (!available) return null; // the theme writes "Sold out" itself
      var template = data.qty > 1 ? ctaMulti : ctaSingle;
      return fillTokens(template, totals, moneyFormat);
    }

    function applyCta(totals, data, available) {
      if (!mainButton) return;
      var label = ctaLabel(totals, data, available);
      state.ctaLabel = label;
      state.ctaDisabled = !!state.blocked;
      ctaApplying = true;
      try {
        if (label !== null) {
          var target = mainButton.querySelector('[data-loading]') || mainButton.querySelector('[data-text] > span') || mainButton;
          if (target.textContent !== label) target.textContent = label;
          var wrap = mainButton.querySelector('[data-text]');
          if (wrap) wrap.setAttribute('data-text', label);
        }
        if (available) {
          if (mainButton.disabled !== state.ctaDisabled) mainButton.disabled = state.ctaDisabled;
          mainButton.classList.toggle('disabled', state.ctaDisabled);
          if (stickyButton && stickyButton.disabled !== state.ctaDisabled) stickyButton.disabled = state.ctaDisabled;
        }
        mainButton.setAttribute('aria-disabled', state.ctaDisabled ? 'true' : 'false');
      } finally {
        if (ctaObserver) ctaObserver.takeRecords();
        ctaApplying = false;
      }
    }

    // The theme rewrites the button's innerHTML and `disabled` on every variant
    // change (after an await, i.e. after our listener). Re-apply our state then.
    if (mainButton && 'MutationObserver' in window) {
      ctaObserver = new MutationObserver(function () {
        if (ctaApplying || !state.ctaLabel) return;
        var current = selectedTier();
        var data = current ? tierData(current) : { qty: 1, discount: 0, offer: 'percent', gift: false };
        var totals = compute(data, current ? piecesFor(current, data) : []);
        applyCta(totals, data, !!(state.variant && state.variant.available));
      });
      ctaObserver.observe(mainButton, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled'] });
    }

    /* ---------- sticky bar ---------- */
    function pushSticky(totals, data, current, available) {
      var variant = state.variant;
      if (!variant) return;
      var pct = totals.pct;
      var original = totals.subtotal;
      var compareAt = Number(variant.compare_at_price || 0);
      if (totals.savings === 0 && data.qty === 1 && compareAt > totals.total) {
        original = compareAt;
        pct = Math.round((compareAt - totals.total) * 100 / compareAt);
      }
      var badge = '';
      if (totals.savings > 0 && totals.bxgy) badge = stickyFreeBadge;
      else if (pct > 0 && original > totals.total) badge = savePrefix + ' ' + pct + '%';
      updateSticky(section, {
        total: totals.total,
        original: original,
        pct: pct,
        qty: data.qty,
        badge: badge,
        label: data.qty > 1 ? fillTokens(stickySummary, totals, moneyFormat) : '',
        button: fillTokens(data.qty > 1 ? stickyMulti : stickySingle, totals, moneyFormat),
        gift: data.gift && giftVariantId && !root.hasAttribute('data-gift-unavailable'),
        available: available,
        blocked: !!state.blocked
      });
    }

    function syncQuantity() {
      var current = selectedTier();
      if (!current) return;
      var data = tierData(current);
      if (qtyInput) {
        qtyInput.value = String(data.qty);
        if (qtyInput === themeQtyInput) {
          qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
      ensureRows(data.qty);
      render();
    }

    /* ---------- mix & match rows ---------- */
    function ensureRows(qty) {
      if (bundleMode !== 'mix') return;
      var main = mainPiece();
      while (state.rows.length < qty - 1) {
        state.rows.push({ handle: currentHandle, variantId: main ? main.id : 0, touched: false });
      }
      // Untouched rows keep mirroring piece 1 so "3 of the same" needs no taps.
      state.rows.forEach(function (row) {
        if (!row.touched && main) {
          row.handle = currentHandle;
          row.variantId = main.id;
        }
      });
    }

    function el(tag, className, text) {
      var node = document.createElement(tag);
      if (className) node.className = className;
      if (text !== undefined && text !== null) node.textContent = text;
      return node;
    }

    function renderMix(current, data, pieces, totals) {
      if (bundleMode !== 'mix' || !mixPanel) return;
      var panelHost = current && current.querySelector('[data-bundle-panel]');
      var mainAvailable = !!(state.variant && state.variant.available);
      if (!current || data.qty < 2 || !panelHost || !mainAvailable) {
        mixPanel.hidden = true;
        mixPanel.innerHTML = '';
        return;
      }
      if (mixPanel.parentNode !== panelHost) panelHost.appendChild(mixPanel);
      mixPanel.hidden = false;
      mixPanel.innerHTML = '';
      var rowsEl = el('div', 'pdp-mix__rows');
      rowsEl.setAttribute('role', 'group');
      rowsEl.setAttribute('aria-label', fillTokens(stickySummary, totals, moneyFormat));
      pieces.forEach(function (piece, index) {
        rowsEl.appendChild(buildRow(piece, index, pieces.length, totals, data));
      });
      mixPanel.appendChild(rowsEl);

      var canMix = mixProducts.length > 1 || (currentProduct && currentProduct.hasOptions && currentProduct.variants.length > 1);
      if (canMix) mixPanel.appendChild(buildMixToggle());
    }

    /* The mixer stays shut by default so the extra pieces do not repeat the size and
       colour controls that are already above. Closing it resets every row to the main
       pick, so what the rows say is always what goes in the bag. */
    function buildMixToggle() {
      var wrap = el('div', 'pdp-mix__toggle-wrap');
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'pdp-mix__toggle' + (state.mixOpen ? ' is-open' : '');
      btn.setAttribute('data-pdp-mix-toggle', '');
      btn.setAttribute('aria-expanded', state.mixOpen ? 'true' : 'false');
      btn.textContent = state.mixOpen ? mixCloseLabel : mixOpenLabel;
      btn.addEventListener('click', function () {
        state.mixOpen = !state.mixOpen;
        if (!state.mixOpen) {
          // Snap every row back to the main pick, or the summary would claim
          // "Same as above" while the row still held a different variant — and
          // that stale variant is what would actually go in the bag.
          // Clearing `touched` is not enough: only ensureRows re-points the rows.
          state.rows.forEach(function (row) { row.touched = false; });
          var openTier = selectedTier();
          ensureRows(openTier ? tierData(openTier).qty : 1);
        }
        render();
        var next = mixPanel.querySelector('[data-pdp-mix-toggle]');
        if (next) {
          try { next.focus({ preventScroll: true }); } catch (e) { /* ignore */ }
        }
      });
      wrap.appendChild(btn);
      return wrap;
    }

    function buildRow(piece, index, qty, totals, data) {
      var isFreeSlot = totals.bxgy && index === qty - 1;
      var row = el('div', 'pdp-mix__row' + (isFreeSlot ? ' pdp-mix__row--free' : '') + (index === 0 ? ' pdp-mix__row--main' : ''));
      row.setAttribute('data-mix-row', String(index));
      var head = el('div', 'pdp-mix__head');
      var label = el('span', 'pdp-mix__label', rowLabel(index, qty, isFreeSlot));
      head.appendChild(label);
      var priceEl = el('span', 'pdp-mix__price');
      if (totals.freeIndex === index) {
        // Strikethrough is silent for screen readers: say "Regular price" before the amount.
        var struck = el('s', 'pdp-mix__price-was');
        struck.appendChild(el('span', 'visually-hidden', regularPriceLabel + ' '));
        struck.appendChild(document.createTextNode(formatMoney(piece.price, moneyFormat)));
        priceEl.appendChild(struck);
        priceEl.appendChild(document.createTextNode(' '));
        priceEl.appendChild(el('strong', 'pdp-mix__price-free', freeLabel));
      } else {
        priceEl.textContent = formatMoney(piece.price, moneyFormat);
      }
      head.appendChild(priceEl);
      row.appendChild(head);

      var body = el('div', 'pdp-mix__body');
      var thumb = el('span', 'pdp-mix__thumb');
      if (piece.product && piece.product.i) {
        var img = document.createElement('img');
        img.src = piece.product.i;
        img.alt = '';
        img.width = 48;
        img.height = 48;
        img.loading = 'lazy';
        thumb.appendChild(img);
      }
      body.appendChild(thumb);

      if (index === 0 || !state.mixOpen) {
        // Piece 1 always mirrors the main picker above, and so does every other piece
        // until the shopper opens the mixer. No second control for the same choice.
        var summary = el('div', 'pdp-mix__summary');
        summary.appendChild(el('span', 'pdp-mix__summary-title', piece.product ? piece.product.t : ''));
        if (piece.title) summary.appendChild(el('span', 'pdp-mix__summary-variant', piece.title));
        summary.appendChild(el('span', 'pdp-mix__summary-note', index === 0 ? pieceSelectedNote : pieceSameNote));
        body.appendChild(summary);
      } else {
        body.appendChild(buildFields(index, piece, qty));
      }
      row.appendChild(body);
      return row;
    }

    function buildFields(index, piece, qty) {
      var fields = el('div', 'pdp-mix__fields');
      var row = state.rows[index - 1];
      var product = piece.product || currentProduct;
      var idBase = 'pdp-mix-' + sectionId + '-' + index;

      if (mixProducts.length > 1) {
        var styleField = el('label', 'pdp-mix__field pdp-mix__field--style');
        styleField.setAttribute('for', idBase + '-style');
        styleField.appendChild(el('span', 'pdp-mix__field-label', styleLabel));
        var styleSelect = document.createElement('select');
        styleSelect.id = idBase + '-style';
        styleSelect.className = 'pdp-mix__select';
        styleSelect.setAttribute('aria-label', rowLabel(index, qty, false) + ' — ' + styleLabel);
        mixProducts.forEach(function (p) {
          var opt = document.createElement('option');
          opt.value = p.h;
          opt.textContent = p.t;
          opt.selected = p.h === product.h;
          styleSelect.appendChild(opt);
        });
        styleSelect.addEventListener('change', function () {
          var next = productByHandle[styleSelect.value];
          if (!next) return;
          var v = firstAvailable(next, piece.variant && piece.variant.product ? piece.variant : null);
          row.handle = next.h;
          row.variantId = v ? v.id : 0;
          row.touched = true;
          render();
          focusField(idBase + '-style');
        });
        styleField.appendChild(styleSelect);
        fields.appendChild(styleField);
      }

      if (product && product.hasOptions) {
        var currentValues = piece.variant && piece.variant.options ? piece.variant.options.slice() : [];
        product.optionNames.forEach(function (name, k) {
          var field = el('label', 'pdp-mix__field');
          var id = idBase + '-opt' + k;
          field.setAttribute('for', id);
          field.appendChild(el('span', 'pdp-mix__field-label', name));
          var select = document.createElement('select');
          select.id = id;
          select.className = 'pdp-mix__select';
          select.setAttribute('aria-label', rowLabel(index, qty, false) + ' — ' + name);
          var values = [];
          product.variants.forEach(function (v) {
            if (values.indexOf(v.options[k]) === -1) values.push(v.options[k]);
          });
          values.forEach(function (value) {
            var opt = document.createElement('option');
            opt.value = value;
            var probe = currentValues.slice();
            probe[k] = value;
            var candidate = variantByOptions(product, probe);
            var text = value;
            var isCurrent = !!(candidate && piece.variant && candidate.id === piece.variant.id);
            if (!candidate || !candidate.available) {
              text += ' (' + soldOutLabel + ')';
              opt.disabled = true;
            } else if (!isCurrent && remainingFor(candidate, index) < 1) {
              // Alternatives that other rows already used up. The row's own choice is
              // never struck here: an over-stock set is explained by the guard message.
              text += ' (' + noStockOption + ')';
              opt.disabled = true;
            }
            opt.textContent = text;
            opt.selected = value === currentValues[k];
            select.appendChild(opt);
          });
          select.addEventListener('change', function () {
            var probe = currentValues.slice();
            probe[k] = select.value;
            var v = variantByOptions(product, probe);
            if (!v) return;
            row.variantId = v.id;
            row.touched = true;
            render();
            focusField(id);
          });
          field.appendChild(select);
          fields.appendChild(field);
        });
      }
      return fields;
    }

    function focusField(id) {
      var node = document.getElementById(id);
      if (node) {
        try { node.focus({ preventScroll: true }); } catch (e) { /* ignore */ }
      }
    }

    /* Units of a variant still free after the OTHER rows (and piece 1) took theirs. */
    function remainingFor(variant, rowIndex) {
      var limit = limitFor(variant);
      if (!isFinite(limit)) return Infinity;
      var taken = 0;
      var main = mainPiece();
      if (main && main.id === variant.id) taken += 1;
      state.rows.forEach(function (r, i) {
        if (i + 1 === rowIndex) return;
        if (i + 1 >= (selectedTier() ? tierData(selectedTier()).qty : 1)) return;
        if (Number(r.variantId) === variant.id) taken += 1;
      });
      return limit - taken;
    }

    /* ---------- events ---------- */
    radios.forEach(function (radio, index) {
      if (!radio) return;
      radio.addEventListener('change', function () {
        if (radio.checked) syncQuantity();
      });
      // Make the whole card keyboard friendly: Enter/Space on the label works natively.
      tiers[index].querySelector('.pdp-bundle__card').addEventListener('click', function () {
        if (radio.disabled) return;
        if (!radio.checked) {
          radio.checked = true;
          syncQuantity();
        }
      });
    });

    var eventBus = bus();
    if (eventBus && sectionId) {
      eventBus.listen('pdp:section-' + sectionId + ':change-variant', function (payload) {
        if (payload && payload.variant) {
          state.variant = payload.variant;
          ensureRows(selectedTier() ? tierData(selectedTier()).qty : 1);
          render();
        }
      });
    }

    window.addEventListener('pageshow', function () {
      // bfcache: never leave a stuck spinner or a disabled button behind.
      state.submitting = false;
      Array.prototype.forEach.call(form.querySelectorAll('[type="submit"]'), function (button) {
        button.classList.remove('is-loading');
      });
      render();
    });

    /* Submit interception — one path for every bundle add so the cart drawer,
       header count and analytics behave exactly like the theme's own ATC. */
    function showError(error) {
      // Only Shopify's own `description` (422: "You can't add more…") is customer copy.
      // Anything else (a 429 challenge page, an offline TypeError) gets the generic text.
      var description = error && typeof error.description === 'string' && error.description.trim()
        ? error.description
        : addErrorText;
      var notifier = window.themeCore && window.themeCore.CartNotificationError;
      var inline = form.querySelector('.js-product-quantity-error');
      var inlineWrapper = form.querySelector('.js-product-quantity-error-wrapper');
      if (inline) {
        inline.textContent = description;
        if (inlineWrapper) inlineWrapper.classList.remove('is-hidden');
        return;
      }
      if (notifier && typeof notifier.addNotification === 'function') {
        try {
          notifier.addNotification(description);
          notifier.open();
        } catch (e) {
          /* ignore */
        }
      }
    }

    /* Line items for the chosen tier: one entry per distinct variant, quantities
       merged. Form properties (gift message, pre-order flag) ride on every line. */
    function collectTierItems(serialized, data, pieces) {
      var props = {};
      Object.keys(serialized).forEach(function (key) {
        if (key.indexOf('properties[') === 0) props[key] = serialized[key];
      });
      var items = [];
      var byId = {};
      pieces.forEach(function (piece) {
        if (byId[piece.id]) {
          byId[piece.id].quantity += 1;
          return;
        }
        var item = { id: piece.id, quantity: 1 };
        Object.keys(props).forEach(function (key) { item[key] = props[key]; });
        byId[piece.id] = item;
        items.push(item);
      });
      if (!items.length) {
        serialized.quantity = data.qty;
        return [serialized];
      }
      return items;
    }

    function buildItems() {
      var utils = window.themeCore && window.themeCore.utils;
      var formData = new FormData(form);
      var serialized = utils && typeof utils.convertFormData === 'function' ? utils.convertFormData(formData) : null;
      if (!serialized || !serialized.id) return null;
      var current = selectedTier();
      var data = current ? tierData(current) : { qty: 1, discount: 0, offer: 'percent', gift: false };
      var pieces = current ? piecesFor(current, data) : [];
      var totals = compute(data, pieces);
      var items = collectTierItems(serialized, data, pieces);
      var giftUnavailable = root.hasAttribute('data-gift-unavailable');
      if (data.gift && giftVariantId && !giftUnavailable) {
        var giftProps = {};
        giftProps['_pdp_bundle_gift'] = 'true';
        giftProps[giftProperty] = giftLabel;
        items.push({ id: giftVariantId, quantity: 1, properties: giftProps });
      }
      var count = pieces.length || data.qty;
      if (data.gift && giftVariantId && !giftUnavailable) count += 1;
      return { items: items, tier: data, totals: totals, count: count };
    }

    function showNotice(text) {
      if (!noticeEl) return;
      noticeEl.textContent = text || '';
      noticeEl.hidden = !text;
    }

    function verifyCart(cart, built, addSucceeded, before) {
      if (!cart || !cart.items) return;

      // 1. Free gift must actually be free; otherwise remove it and stop claiming it.
      var giftLine = cart.items.filter(function (item) {
        return item.properties && item.properties._pdp_bundle_gift === 'true';
      })[0];
      if (giftLine && Number(giftLine.final_line_price) > 0) {
        console.warn('[PDP bundles] The free-gift product was added at full price because no automatic "Buy X get Y" discount makes it free. Removing it from the cart and hiding the gift offer for this session. Create the discount in Shopify admin (Discounts → Create discount → Buy X get Y → Automatic) to enable it.');
        session(SESSION_GIFT_FLAG, '1');
        root.setAttribute('data-gift-unavailable', '');
        var api = window.themeCore.CartApi;
        api.makeRequest(api.actions.CHANGE_CART_ITEM_QUANTITY, giftLine.key, 0, '', { noOpen: true })
          .catch(function () {})
          .then(function () {
            // Overlapping drawer refreshes can resolve out of order; make the final state win.
            window.setTimeout(function () {
              if (bus()) bus().emit('cart:refresh');
            }, 700);
          });
      }

      // 2. Promise check (only after a successful add, never after a stock error).
      //    `before` is the cart as it was right before this add, so the price of THIS
      //    set is the difference in cart.total_price — whatever else was in the bag.
      if (!addSucceeded || !built || !built.tier || built.tier.qty < 2) return;
      var promised = Number(built.totals.total || 0);
      var promisedSaving = Number(built.totals.savings || 0);
      var cartTotal = Number(cart.total_price || 0);
      var cartDiscount = Math.floor(Number(cart.total_discount || 0));
      var onlyOurs = Number(cart.item_count || 0) === built.count;
      var hasBefore = !!(before && typeof before.total_price === 'number');
      var charged = NaN;
      if (hasBefore) charged = cartTotal - Number(before.total_price || 0);
      else if (onlyOurs) charged = cartTotal;
      var broken = false;
      var offerUsed = false;
      if (!isNaN(charged)) {
        // The customer may pay the same or LESS for this set, never more.
        broken = charged > promised;
        // A product discount is in the bag but did not stretch to this set: the offer
        // was already used (e.g. "one use per order"), not missing.
        offerUsed = broken && lineDiscountTotal(cart) > 0;
      } else if (promisedSaving > 0) {
        // No snapshot and other items in the bag: only sanity-check that a saving exists at all.
        broken = cartDiscount <= 0;
      }
      try {
        window.sessionStorage.setItem('pdp:bundle-promised', JSON.stringify({ total: promised, charged: charged, at: Date.now() }));
      } catch (e) { /* ignore */ }
      if (broken) {
        if (offerUsed) {
          console.warn('[PDP bundles] Promised ' + promised + ' cents for ' + built.tier.qty + ' pieces but this add cost ' + charged + ': the automatic discount is already used in this cart (discount ' + cartDiscount + '). Savings labels are hidden until the bag no longer holds the discounted set.');
          session(SESSION_OFFER_USED_FLAG, '1');
        } else {
          console.warn('[PDP bundles] Promised ' + promised + ' cents for ' + built.tier.qty + ' pieces but the cart totals ' + cartTotal + ' (this add ' + charged + ', discount ' + cartDiscount + '). Savings labels are now hidden for this session. Check Shopify admin → Discounts.');
          session(SESSION_DISCOUNT_FLAG, '1');
        }
        root.setAttribute('data-discount-unverified', '');
        showNotice(offerUsed ? usedNotice : verifyNotice);
        render();
      }
    }

    /* After a multi-piece add the page goes back to the first tier: the set is in the
       bag, and a promise computed for the bag as it was must not linger on the button. */
    function resetSelection() {
      if (!radios[0] || radios[0].disabled || radios[0].checked) return;
      radios[0].checked = true;
      syncQuantity();
    }

    function onSubmitCapture(event) {
      if (event.target !== form) return;
      if (event.defaultPrevented) return;
      // Personalised pieces: the theme's inline guard blocks submits until an initial is chosen.
      var initialOption = form.querySelector('.js-initial-option');
      if (initialOption && !initialOption.querySelector('input[type="radio"]:checked')) return;
      var api = window.themeCore && window.themeCore.CartApi;
      if (!api || !api.actions) return; // theme not ready: let the native path run
      if (state.submitting || state.blocked) {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (state.blocked && blockEl) {
          blockEl.hidden = false;
          try { blockEl.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch (e) { /* ignore */ }
        }
        return;
      }
      var built;
      try {
        built = buildItems();
      } catch (e) {
        return;
      }
      if (!built) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      state.submitting = true;
      showNotice('');

      var submitButtons = Array.prototype.slice.call(form.querySelectorAll('[type="submit"]'));
      submitButtons.forEach(function (button) {
        button.classList.add('is-loading');
      });
      var inlineWrapper = form.querySelector('.js-product-quantity-error-wrapper');
      if (inlineWrapper) inlineWrapper.classList.add('is-hidden');

      var addSucceeded = false;
      // Multi-piece sets are checked against what THIS add costs, so read the bag first.
      var snapshot = built.tier.qty > 1 ? readCartSnapshot() : Promise.resolve(null);
      snapshot
        .then(function (before) {
          return api.makeRequest(api.actions.ADD_TO_CART_MANY, built.items)
            .then(function () {
              addSucceeded = true;
              var inline = form.querySelector('.js-product-quantity-error');
              if (inline) inline.textContent = '';
            })
            .catch(function (error) {
              showError(error);
            })
            .then(function () {
              // Always re-read the cart: Shopify can add part of the items and still answer 422.
              return api.makeRequest(api.actions.GET_CART, { noOpen: true }).then(function (cart) {
                verifyCart(cart, built, addSucceeded, before);
              }).catch(function () {});
            });
        })
        .then(function () {
          if (addSucceeded && built.tier.qty > 1) resetSelection();
          window.setTimeout(function () {
            state.submitting = false;
            submitButtons.forEach(function (button) {
              button.classList.remove('is-loading');
            });
          }, 400);
        });
    }

    // Document-level capture always runs before target-phase listeners (the theme's own
    // submit handler is bound on the form), in every engine.
    document.addEventListener('submit', onSubmitCapture, true);

    syncQuantity();
  }

  /* --------------------------------------------------------------------------
     Sticky bar: show the selected variant under the title
     -------------------------------------------------------------------------- */
  function initStickyMeta(el) {
    if (!el || el.dataset.pdpReady === 'true') return;
    el.dataset.pdpReady = 'true';
    var section = el.closest('[data-section-type="product"]');
    var form = section && section.querySelector('form[data-js-product-form]');
    var sectionId = section && section.getAttribute('data-section-id');

    function update(variant) {
      if (!variant || !variant.title || variant.title === 'Default Title') {
        el.textContent = '';
      } else {
        el.textContent = variant.title;
      }
      var bar = el.closest('[data-pdp-sticky]');
      if (bar) composeStickyLine(bar);
    }

    update(readSelectedVariant(form));
    var eventBus = bus();
    if (eventBus && sectionId) {
      eventBus.listen('pdp:section-' + sectionId + ':change-variant', function (payload) {
        update(payload && payload.variant);
      });
    }
  }

  /* --------------------------------------------------------------------------
     Desktop buy box: the theme makes .product-form position: sticky. When the
     column is taller than the viewport, pin by the bottom edge instead.
     -------------------------------------------------------------------------- */
  function initStickyBuyBox(form) {
    if (!form || form.dataset.pdpStickyReady === 'true') return;
    form.dataset.pdpStickyReady = 'true';
    var mq = window.matchMedia('(min-width: 1200px)');
    var raf = 0;

    function headerHeight() {
      var v = getComputedStyle(document.documentElement).getPropertyValue('--header-height-sticky') || getComputedStyle(document.documentElement).getPropertyValue('--header-height');
      var n = parseFloat(v);
      return isNaN(n) ? 0 : n;
    }

    function update() {
      raf = 0;
      if (!mq.matches) {
        form.style.top = '';
        return;
      }
      var available = window.innerHeight - headerHeight() - 24;
      var height = form.offsetHeight;
      if (height > available) {
        form.style.top = Math.round(available - height + headerHeight()) + 'px';
      } else {
        form.style.top = '';
      }
    }

    function schedule() {
      if (raf) return;
      raf = window.requestAnimationFrame(update);
    }

    if ('ResizeObserver' in window) {
      new ResizeObserver(schedule).observe(form);
    }
    window.addEventListener('resize', schedule);
    form.addEventListener('toggle', schedule, true);
    if (typeof mq.addEventListener === 'function') mq.addEventListener('change', schedule);
    schedule();
  }

  /* --------------------------------------------------------------------------
     Sticky bar consistency: the theme toggles it from an IntersectionObserver,
     which never fires when the page jumps straight past the button (back-to-top,
     anchors). Re-check on scroll so the bar always matches the button position.
     -------------------------------------------------------------------------- */
  function initStickyBarGuard(section) {
    if (!section || section.dataset.pdpStickyGuard === 'true') return;
    section.dataset.pdpStickyGuard = 'true';
    var bar = section.querySelector('.js-sticky-add-to-cart');
    var form = section.querySelector('form[data-js-product-form]');
    var button = form && form.querySelector('.add-to-cart[type="submit"]');
    if (!bar || !button) return;
    var ticking = false;
    function check() {
      ticking = false;
      var rect = button.getBoundingClientRect();
      var shouldShow = rect.bottom < 0;
      if (bar.classList.contains('is-active') !== shouldShow) {
        bar.classList.toggle('is-active', shouldShow);
      }
    }
    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(check);
    }, { passive: true });
  }

  /* --------------------------------------------------------------------------
     Sticky bar (jewelry): thumbnail, short title, bundle, gift, final price,
     savings badge and "Add to bag — $XX.XX". The bundle module pushes totals;
     without bundles the variant price drives it.
     -------------------------------------------------------------------------- */
  var stickyState = new WeakMap();

  function updateSticky(section, data) {
    var bar = section && section.querySelector('[data-pdp-sticky]');
    if (!bar) return;
    var moneyFormat = bar.dataset.moneyFormat || '${{amount}}';
    var label = bar.dataset.label || 'Add to bag';
    var soldOut = bar.dataset.soldOutLabel || 'Sold out';
    var savePrefix = bar.dataset.savePrefix || 'Save';
    var priceEl = bar.querySelector('[data-pdp-sticky-price]');
    var compareEl = bar.querySelector('[data-pdp-sticky-compare]');
    var badgeEl = bar.querySelector('[data-pdp-sticky-savings]');
    var bundleEl = bar.querySelector('[data-pdp-sticky-bundle]');
    var giftEl = bar.querySelector('[data-pdp-sticky-gift]');
    var labelEl = bar.querySelector('[data-pdp-sticky-label]');
    var button = bar.querySelector('.js-sticky-add-to-cart-button');

    if (priceEl) priceEl.textContent = formatMoney(data.total, moneyFormat);
    if (compareEl) {
      var showCompare = data.original > data.total;
      compareEl.textContent = showCompare ? formatMoney(data.original, moneyFormat) : '';
      compareEl.hidden = !showCompare;
    }
    if (badgeEl) {
      var badgeText = typeof data.badge === 'string' ? data.badge : (data.pct > 0 && data.original > data.total ? savePrefix + ' ' + data.pct + '%' : '');
      badgeEl.textContent = badgeText;
      badgeEl.hidden = !badgeText;
    }
    if (bundleEl) bundleEl.textContent = data.label || '';
    if (giftEl) giftEl.hidden = !data.gift;
    // With a multi-piece set the variant of piece 1 alone would mislead: show the summary only.
    bar.classList.toggle('pdp-sticky--multi', data.qty > 1);
    composeStickyLine(bar);
    if (labelEl) {
      var text = data.available === false ? soldOut : (data.button || (label + ' \u2014 ' + formatMoney(data.total, moneyFormat)));
      labelEl.textContent = text;
      if (button) button.setAttribute('aria-label', text);
    }
    stickyState.set(section, data);
  }

  function composeStickyLine(bar) {
    var line = bar.querySelector('[data-pdp-sticky-line]');
    if (!line) return;
    var variantEl = bar.querySelector('[data-pdp-sticky-variant]');
    var bundleEl = bar.querySelector('[data-pdp-sticky-bundle]');
    var multi = bar.classList.contains('pdp-sticky--multi');
    var parts = [];
    if (!multi && variantEl && variantEl.textContent.trim()) parts.push(variantEl.textContent.trim());
    if (bundleEl && bundleEl.textContent.trim()) parts.push(bundleEl.textContent.trim());
    line.textContent = parts.join(' \u00b7 ');
    line.hidden = parts.length === 0;
  }

  function initStickyPrice(bar) {
    if (!bar || bar.dataset.pdpReady === 'true') return;
    bar.dataset.pdpReady = 'true';
    var section = bar.closest('[data-section-type="product"]');
    var form = section && section.querySelector('form[data-js-product-form]');
    var sectionId = section && section.getAttribute('data-section-id');
    var hasBundles = !!(section && section.querySelector('[data-pdp-bundles]'));

    function fromVariant(variant) {
      if (!variant) return;
      var price = Number(variant.price);
      var compareAt = Number(variant.compare_at_price || 0);
      var pct = compareAt > price ? Math.round((compareAt - price) * 100 / compareAt) : 0;
      updateSticky(section, { total: price, original: compareAt > price ? compareAt : price, pct: pct, qty: 1, label: '', gift: false, available: !!variant.available });
    }

    // With the bundle selector present, it owns the totals (it listens to the same event).
    if (!hasBundles) {
      fromVariant(readSelectedVariant(form));
      var eventBus = bus();
      if (eventBus && sectionId) {
        eventBus.listen('pdp:section-' + sectionId + ':change-variant', function (payload) {
          fromVariant(payload && payload.variant);
        });
      }
    }
  }

  /* --------------------------------------------------------------------------
     Demand module: default vs. real low-stock state per selected variant
     -------------------------------------------------------------------------- */
  function initDemand(el) {
    if (!el || el.dataset.pdpReady === 'true') return;
    el.dataset.pdpReady = 'true';
    var section = el.closest('[data-section-type="product"]');
    var form = section && section.querySelector('form[data-js-product-form]');
    var sectionId = section && section.getAttribute('data-section-id');
    var threshold = parseInt(el.dataset.threshold, 10) || 5;
    var template = el.dataset.lowStatus || 'Only [count] remaining';
    var showDefault = el.dataset.defaultState === 'true';
    var defaultEl = el.querySelector('[data-pdp-demand-default]');
    var lowEl = el.querySelector('[data-pdp-demand-low]');
    var countEl = el.querySelector('[data-pdp-demand-count]');

    function update(variant) {
      if (!variant) return;
      var info = stockInfo(section, variant);
      var state = showDefault ? 'default' : 'hidden';
      if (!variant.available) {
        state = 'hidden';
      } else if (info.tracked && isFinite(info.limit) && !isNaN(info.quantity) && info.quantity > 0 && info.quantity <= threshold) {
        state = 'low';
      }
      el.hidden = state === 'hidden';
      el.classList.toggle('pdp-demand--low', state === 'low');
      el.classList.toggle('pdp-demand--default', state === 'default');
      if (defaultEl) defaultEl.hidden = state !== 'default';
      if (lowEl) lowEl.hidden = state !== 'low';
      if (countEl && state === 'low') countEl.textContent = template.replace(/\[count\]/g, String(info.quantity));
    }

    update(readSelectedVariant(form));
    var eventBus = bus();
    if (eventBus && sectionId) {
      eventBus.listen('pdp:section-' + sectionId + ':change-variant', function (payload) {
        update(payload && payload.variant);
      });
    }
  }

  /* --------------------------------------------------------------------------
     Promo module: hide after the configured end (covers cached pages)
     -------------------------------------------------------------------------- */
  function initPromo(el) {
    if (!el || el.dataset.pdpReady === 'true') return;
    el.dataset.pdpReady = 'true';
    var endsAt = Number(el.dataset.endsAt || 0);
    if (!endsAt) return;
    function check() {
      if (Date.now() / 1000 > endsAt) {
        var block = el.closest('.pdp-block') || el;
        block.hidden = true;
      }
    }
    check();
    window.setInterval(check, 60000);
  }

  /* --------------------------------------------------------------------------
     Estimated delivery: "Sep 24 – Sep 26" counted from the shopper's today.
     Worked out here (not in Liquid) because product pages are cached.
     Hidden while the selected variant is sold out.
     -------------------------------------------------------------------------- */
  function etaAddDays(from, days, skipWeekends) {
    var d = new Date(from.getFullYear(), from.getMonth(), from.getDate(), 12);
    if (!skipWeekends) {
      d.setDate(d.getDate() + days);
      return d;
    }
    var added = 0;
    while (added < days) {
      d.setDate(d.getDate() + 1);
      if (d.getDay() !== 0 && d.getDay() !== 6) added++;
    }
    return d;
  }

  function etaFormat(date, style, locale) {
    var opts = { month: style === 'long' ? 'long' : 'short', day: 'numeric' };
    if (style === 'weekday') opts.weekday = 'short';
    try {
      return new Intl.DateTimeFormat(locale || undefined, opts).format(date);
    } catch (e) {
      return new Intl.DateTimeFormat('en-US', opts).format(date);
    }
  }

  function etaRange(today, minDays, maxDays, skipWeekends, style, locale) {
    var lo = Math.min(minDays, maxDays);
    var hi = Math.max(minDays, maxDays);
    var first = etaFormat(etaAddDays(today, lo, skipWeekends), style, locale);
    if (lo === hi) return first;
    return first + ' \u2013 ' + etaFormat(etaAddDays(today, hi, skipWeekends), style, locale);
  }

  function initDeliveryEstimate(el) {
    if (!el || el.dataset.pdpReady === 'true') return;
    el.dataset.pdpReady = 'true';
    var minDays = parseInt(el.dataset.min, 10);
    var maxDays = parseInt(el.dataset.max, 10);
    if (isNaN(minDays) || isNaN(maxDays)) return;
    var skip = el.dataset.skipWeekends === 'true';
    var style = el.dataset.dateStyle || 'short';
    var locale = el.dataset.locale || document.documentElement.lang || 'en';
    var out = el.querySelector('[data-pdp-eta-dates]');

    function render() {
      if (out) out.textContent = etaRange(new Date(), minDays, maxDays, skip, style, locale);
    }
    render();
    // A tab left open past midnight (or woken from the background / bfcache)
    // must not keep yesterday's window.
    window.setInterval(render, 60 * 1000);
    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) render();
    });
    window.addEventListener('pageshow', render);

    var section = el.closest('[data-section-type="product"]');
    var sectionId = section && section.getAttribute('data-section-id');
    var eventBus = bus();
    if (eventBus && sectionId) {
      eventBus.listen('pdp:section-' + sectionId + ':change-variant', function (payload) {
        var variant = payload && payload.variant;
        el.hidden = !!(variant && !variant.available);
      });
    }
  }

  // Exposed for QA only (pure date maths, no side effects).
  window.__pdpEtaRange = etaRange;

  /* --------------------------------------------------------------------------
     Caption under the gallery: swap the line when the option value changes
     (e.g. a gold-plating line for Gold that must not show on Silver).
     -------------------------------------------------------------------------- */
  function initCaption(el) {
    if (!el || el.dataset.pdpReady === 'true') return;
    el.dataset.pdpReady = 'true';
    var node = el.querySelector('[data-pdp-caption-text]');
    if (!node) return;
    var map;
    try {
      map = JSON.parse(el.getAttribute('data-caption-map') || '{}');
    } catch (e) {
      return;
    }
    var fallback = node.textContent;
    var section = el.closest('[data-section-type="product"]');
    var sectionId = section && section.getAttribute('data-section-id');
    var eventBus = bus();
    if (!eventBus || !sectionId) return;
    eventBus.listen('pdp:section-' + sectionId + ':change-variant', function (payload) {
      var variant = payload && payload.variant;
      if (!variant || !variant.options) return;
      var next = null;
      for (var i = 0; i < variant.options.length; i++) {
        var key = String(variant.options[i]).trim().toLowerCase();
        if (Object.prototype.hasOwnProperty.call(map, key)) next = map[key];
      }
      node.textContent = next || fallback;
    });
  }

  function initAll() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-pdp-sticky-variant]'), initStickyMeta);
    Array.prototype.forEach.call(document.querySelectorAll('[data-pdp-sticky]'), initStickyPrice);
    Array.prototype.forEach.call(document.querySelectorAll('[data-pdp-demand]'), initDemand);
    Array.prototype.forEach.call(document.querySelectorAll('[data-pdp-promo]'), initPromo);
    Array.prototype.forEach.call(document.querySelectorAll('[data-pdp-eta]'), initDeliveryEstimate);
    Array.prototype.forEach.call(document.querySelectorAll('[data-pdp-caption]'), initCaption);
    Array.prototype.forEach.call(document.querySelectorAll('[data-pdp-bundles]'), initBundles);
    Array.prototype.forEach.call(document.querySelectorAll('[data-section-type="product"] form[data-js-product-form]'), initStickyBuyBox);
    Array.prototype.forEach.call(document.querySelectorAll('[data-section-type="product"]'), initStickyBarGuard);
  }

  function boot() {
    whenThemeReady(initAll);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  // Theme Editor: sections re-render, so re-scan for new nodes.
  document.addEventListener('shopify:section:load', function (event) {
    // The theme may replace the section node after this fires; wait a tick,
    // drop the "ready" flags that cloneNode carried over, then re-scan.
    window.setTimeout(function () {
      var scope = (event && event.target && event.target.querySelectorAll) ? event.target : document;
      ['data-pdp-bundles-ready', 'data-pdp-ready', 'data-pdp-sticky-ready', 'data-pdp-sticky-guard'].forEach(function (attr) {
        Array.prototype.forEach.call(scope.querySelectorAll('[' + attr + ']'), function (el) { el.removeAttribute(attr); });
      });
      stockCache = new WeakMap();
      whenThemeReady(initAll);
    }, 60);
  });
})();
